//context/AuthContext.tsx
import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { User } from '../types/types';
import { userPool, COGNITO_CLIENT_ID } from '../services/cognitoConfig';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import {
    getLoggedInAccounts,
    touchLoggedInAccount,
    removeLoggedInAccount,
    LoggedInAccountInfo,
} from '../services/accountRegistry';

interface AuthContextProps {
    isLoggedIn: boolean;
    login: (userInfo: User) => void;
    logout: () => void;
    user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    userSub: string | null;
    idToken: string | null;
    isAdmin: boolean;
    refreshIdToken: () => Promise<string | null>;
    loggedInAccounts: LoggedInAccountInfo[];
    refreshLoggedInAccounts: () => void;
    switchAccount: (username: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const storedUser = localStorage.getItem("userInfo");
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const [userSub, setUserSub] = useState<string | null>(null);
    const [idToken, setIdToken] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [loggedInAccounts, setLoggedInAccounts] = useState<LoggedInAccountInfo[]>(() => getLoggedInAccounts());

    const refreshLoggedInAccounts = useCallback(() => {
        setLoggedInAccounts(getLoggedInAccounts());
    }, []);

    // セッションが取れたときの共通処理: state更新 + アカウント一覧への登録/最終ログイン日時更新
    //
    // 以前は idToken/userSub だけを更新し user state は更新していなかったため、
    // ページ読み込み時に localStorage の古い userInfo（"name" クレームがセットされて
    // いないアカウントだと username が空文字列になっていた）がずっと使われ続け、
    // アカウント切替メニューのログアウトボタンが「@からログアウト」と表示される
    // バグになっていた。毎回 user state も cognito:username 由来の値で上書きする。
    const applySession = useCallback((session: CognitoUserSession) => {
        const payload = session.getIdToken().payload;
        const sub = payload.sub as string;
        // "name" クレームではなく "cognito:username" を正とする（Header等の既存実装と同じ方針。
        // 前者はログインIDと一致しない場合があり、CognitoUser の再構築に使えないため）
        const username = (payload['cognito:username'] as string) || (payload.name as string);
        const email = payload.email as string | undefined;
        // Cognito Groupsに所属していると付与されるクレーム（admin.readlinker.com用）
        const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];

        setIdToken(session.getIdToken().getJwtToken());
        setUserSub(sub);
        setIsAdmin(groups.includes('admin'));
        setUser((prev) => {
            const updated: User = { username, sub, email: email ?? prev?.email ?? '' };
            localStorage.setItem('userInfo', JSON.stringify(updated));
            return updated;
        });
        touchLoggedInAccount(sub, username);
        refreshLoggedInAccounts();

        return { sub, username, email };
    }, [refreshLoggedInAccounts]);

    // 指定ユーザー名のアカウントへ切り替える。トークンは amazon-cognito-identity-js が
    // ユーザー名ごとに保持しているので、そこから読み出す（無ければリフレッシュトークンで再取得）。
    const switchAccount = useCallback((username: string): Promise<boolean> => {
        return new Promise((resolve) => {
            // getSession内部でトークン失効時にrefreshSessionへ進み、その先頭で
            // LastAuthUserを読み直してthis.usernameを上書きしてしまう実装になっているため、
            // 先にLastAuthUserを切替先へ書き換えておくことで意図しない巻き戻りを防ぐ
            const lastUserKey = `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}.LastAuthUser`;
            localStorage.setItem(lastUserKey, username);

            const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
            cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
                if (err || !session || !session.isValid()) {
                    console.warn('Failed to switch account:', err?.message);
                    const stale = getLoggedInAccounts().find((a) => a.username === username);
                    if (stale) removeLoggedInAccount(stale.sub);
                    refreshLoggedInAccounts();
                    resolve(false);
                    return;
                }
                applySession(session);
                resolve(true);
            });
        });
    }, [applySession, refreshLoggedInAccounts]);

    // ログアウト・セッション失効時の共通処理: 対象アカウントを一覧から外し、
    // 他に残っているアカウントがあれば最後にログインしたものへ自動的に切り替える
    // （X等と同様、ブラウザ全体からは即ログアウトしない）
    const deauthenticateAndMaybeSwitch = useCallback((username: string | null) => {
        if (username) {
            const stale = getLoggedInAccounts().find((a) => a.username === username);
            if (stale) removeLoggedInAccount(stale.sub);
        }
        localStorage.removeItem('userInfo');
        setUser(null);
        setIdToken(null);
        setUserSub(null);
        setIsAdmin(false);
        refreshLoggedInAccounts();

        const remaining = getLoggedInAccounts();
        if (remaining.length > 0) {
            switchAccount(remaining[0].username);
        }
    }, [refreshLoggedInAccounts, switchAccount]);

    const refreshIdToken = useCallback(async (): Promise<string | null> => {
        return new Promise((resolve) => {
            const cognitoUser = userPool.getCurrentUser();
            if (cognitoUser) {
                cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
                    if (err) {
                        // リフレッシュトークン期限切れ等。クラッシュさせず、
                        // 他に有効なアカウントがあればそちらへ自動切替する
                        console.warn('Session refresh failed, signing out:', err.message);
                        const staleUsername = cognitoUser.getUsername();
                        cognitoUser.signOut();
                        deauthenticateAndMaybeSwitch(staleUsername);
                        resolve(null);
                        return;
                    }
                    if (session && session.isValid()) {
                        applySession(session);
                        resolve(session.getIdToken().getJwtToken());
                    } else {
                        console.warn('Session is invalid');
                        resolve(null);
                    }
                });
            } else {
                console.warn('No current user');
                resolve(null);
            }
        });
    }, [applySession, deauthenticateAndMaybeSwitch]);

    useEffect(() => {
        refreshIdToken();
    }, [refreshIdToken]);

    // IDトークン（既定で1時間失効）を定期的に・タブ復帰時に更新する。
    // 以前は初回マウント時の1回しか呼んでいなかったため、タブを1時間以上
    // 開きっぱなしにすると「ログイン中に見えるのに書き込み系APIが401」に
    // なっていた（2026-08-29、GraphPageのお気に入りトグルで発覚）。
    // getSession()はamazon-cognito-identity-js側でトークン失効時に
    // 自動的にリフレッシュトークンを使って再取得してくれるため、定期的に
    // 呼ぶだけでよい。
    useEffect(() => {
        const intervalId = window.setInterval(() => {
            refreshIdToken();
        }, 30 * 60 * 1000); // 30分ごと（既定の失効1時間より十分手前）

        const handleVisibilityChange = () => {
            // スリープ復帰・バックグラウンドタブ復帰時はタイマーが間引かれている
            // ことがあるため、フォアグラウンドに戻ったタイミングでも念のため更新する
            if (document.visibilityState === 'visible') {
                refreshIdToken();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshIdToken]);

    const isLoggedIn = Boolean(user && idToken);

    const login = useCallback((userInfo: User) => {
        console.log("Logged in");
        setUser(userInfo);
        setUserSub(userInfo.sub);
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
        refreshIdToken();
    }, [refreshIdToken]);

    const logout = useCallback(() => {
        const cognitoUser = userPool.getCurrentUser();
        const username = cognitoUser ? cognitoUser.getUsername() : null;
        if (cognitoUser) {
            cognitoUser.signOut();
        }
        deauthenticateAndMaybeSwitch(username);
    }, [deauthenticateAndMaybeSwitch]);

    const contextValue: AuthContextProps = {
        isLoggedIn,
        user,
        setUser,
        login,
        logout,
        userSub,
        idToken,
        isAdmin,
        refreshIdToken,
        loggedInAccounts,
        refreshLoggedInAccounts,
        switchAccount,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
